<script setup>
// My Library — the consumer-facing card grid of a user's DB-backed (authored)
// decks, from GET /api/library/decks. The authoring/management home: create a
// deck, open one for detail/generation, or jump to Practice once it's ready.
// (Distinct from the Voice Quiz play surface at /quizzes.)
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const loading = ref(true);
const error = ref("");
const decks = ref([]);

// status → Bootstrap badge style + label. Mirrors the deck lifecycle
// (draft/queued/generating/ready/error) from the generation queue.
const STATUS = {
  draft:      { cls: "bg-secondary",        label: "Draft" },
  queued:     { cls: "bg-info text-dark",   label: "Queued" },
  generating: { cls: "bg-warning text-dark", label: "Generating…" },
  ready:      { cls: "bg-success",          label: "Ready" },
  error:      { cls: "bg-danger",           label: "Error" }
};
const badge = (s) => STATUS[s] || { cls: "bg-secondary", label: s || "—" };

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const r = await fetch("/api/library/decks", { credentials: "include" });
    if (!r.ok) throw new Error(`/api/library/decks responded ${r.status}`);
    decks.value = (await r.json()).decks || [];
  } catch (e) {
    console.error("[library] list failed:", e); // technical detail to console only
    error.value = "We couldn't load your library just now."; // calm, user-facing
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div class="container-fluid p-0">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h1 class="h3 mb-1">My Library</h1>
        <p class="text-muted mb-0">Decks you've created. Build one, generate its audio, then practise.</p>
      </div>
      <div class="d-flex gap-2">
        <router-link to="/library/import" class="btn btn-outline-secondary">
          <i class="bi bi-filetype-csv me-1"></i>Import CSV
        </router-link>
        <router-link to="/library/new" class="btn btn-primary">
          <i class="bi bi-plus-lg me-1"></i>New deck
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="text-muted py-4">
      <span class="spinner-border spinner-border-sm me-2"></span>Loading your library…
    </div>

    <!-- Calm, recoverable error state — not an alarm. -->
    <div v-else-if="error" class="card border-0 shadow-sm">
      <div class="card-body text-center py-5">
        <i class="bi bi-arrow-clockwise display-6 text-muted d-block mb-3"></i>
        <h2 class="h6">{{ error }}</h2>
        <p class="text-muted small mb-3">This is usually momentary.</p>
        <button class="btn btn-outline-secondary btn-sm" @click="load">
          <i class="bi bi-arrow-clockwise me-1"></i>Try again
        </button>
      </div>
    </div>

    <!-- Warm, inviting empty state. -->
    <div v-else-if="decks.length === 0" class="card border-0 shadow-sm">
      <div class="card-body text-center py-5">
        <i class="bi bi-collection display-6 text-primary opacity-50 d-block mb-3"></i>
        <h2 class="h5">Create your first deck</h2>
        <p class="text-muted">Your library is where your own decks live. Build one by hand or import a CSV, generate the audio, then practise it hands-free.</p>
        <div class="d-flex gap-2 justify-content-center">
          <router-link to="/library/new" class="btn btn-primary">
            <i class="bi bi-plus-lg me-1"></i>New deck
          </router-link>
          <router-link to="/library/import" class="btn btn-outline-secondary">
            <i class="bi bi-filetype-csv me-1"></i>Import CSV
          </router-link>
        </div>
      </div>
    </div>

    <div v-else class="row g-3">
      <div v-for="d in decks" :key="d.id" class="col-12 col-md-6 col-lg-4">
        <div class="card border-0 shadow-sm h-100" :class="{ 'rm-archived': d.archivedAt }">
          <img v-if="d.cover && d.cover.imageUrl" :src="d.cover.imageUrl" alt="" class="rm-lib-cover" loading="lazy">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-start justify-content-between mb-1">
              <h2 class="h6 mb-0 text-truncate" :title="d.topic">{{ d.topic }}</h2>
              <span class="badge ms-2 flex-shrink-0" :class="d.archivedAt ? 'bg-secondary' : badge(d.status).cls">{{ d.archivedAt ? "Archived" : badge(d.status).label }}</span>
            </div>
            <div class="text-muted small mb-2">
              <span v-if="d.area"><i class="bi bi-tag me-1"></i>{{ d.area }} · </span>
              {{ d.itemCount }} question{{ d.itemCount === 1 ? "" : "s" }}
            </div>
            <div class="text-muted small mb-3">Updated {{ fmtDate(d.updatedAt) }}</div>

            <div class="mt-auto d-flex gap-2">
              <router-link :to="`/library/${d.id}`" class="btn btn-sm btn-outline-secondary flex-fill">
                <i class="bi bi-pencil-square me-1"></i>Open
              </router-link>
              <button
                v-if="d.status === 'ready'"
                class="btn btn-sm btn-primary flex-fill"
                @click="router.push('/topics/play/' + encodeURIComponent('lib:' + d.id))"
                title="Practise this deck"
              >
                <i class="bi bi-mic-fill me-1"></i>Practise
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rm-lib-cover {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  object-position: right center;
  border-top-left-radius: var(--bs-card-border-radius, .5rem);
  border-top-right-radius: var(--bs-card-border-radius, .5rem);
}
.rm-archived { opacity: .68; }
</style>
